import { useEffect, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useToast } from '../../contexts/ToastContext'
import AdminLayout from '../../components/admin/AdminLayout'
import { supabase } from '../../lib/supabase'
import {
  Search,
  Filter,
  Calendar,
  User,
  Activity,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Download,
  FileText
} from 'lucide-react'

interface AuditLog {
  id: string
  user_id: string
  action: string
  resource_type: string
  resource_id: string
  details: any
  ip_address: string
  user_agent: string
  created_at: string
  profiles?: {
    email: string
    full_name: string
  }
}

export default function AuditLogs() {
  const { hasPermission } = useAuth()
  const toast = useToast()
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterAction, setFilterAction] = useState<string>('all')
  const [filterResource, setFilterResource] = useState<string>('all')
  const [expandedLog, setExpandedLog] = useState<string | null>(null)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv')

  useEffect(() => {
    loadLogs()
  }, [])

  const loadLogs = async () => {
    try {
      setLoading(true)
      let query = supabase
        .from('audit_logs')
        .select('*, profiles(email, full_name)')
        .order('created_at', { ascending: false })
        .limit(100)

      const { data, error } = await query

      if (error) throw error
      setLogs(data || [])
    } catch (error: any) {
      console.error('Error loading logs:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.profiles?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.resource_type?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesAction = filterAction === 'all' || log.action === filterAction
    const matchesResource = filterResource === 'all' || log.resource_type === filterResource
    
    let matchesDate = true
    if (dateFrom) {
      matchesDate = matchesDate && new Date(log.created_at) >= new Date(dateFrom)
    }
    if (dateTo) {
      matchesDate = matchesDate && new Date(log.created_at) <= new Date(dateTo)
    }

    return matchesSearch && matchesAction && matchesResource && matchesDate
  })

  const uniqueActions = Array.from(new Set(logs.map(log => log.action)))
  const uniqueResources = Array.from(new Set(logs.map(log => log.resource_type).filter(Boolean)))

  const getActionBadge = (action: string) => {
    const colors: Record<string, string> = {
      'ASSIGN_ROLE': 'bg-blue-100 text-blue-800',
      'REMOVE_ROLE': 'bg-purple-100 text-purple-800',
      'SUSPEND_USER': 'bg-red-100 text-red-800',
      'ACTIVATE_USER': 'bg-green-100 text-green-800',
      'RESET_PASSWORD': 'bg-yellow-100 text-yellow-800',
      'DELETE_COURSE': 'bg-orange-100 text-orange-800',
      'PUBLISH_COURSE': 'bg-teal-100 text-teal-800'
    }
    return colors[action] || 'bg-gray-100 text-gray-800'
  }

  const exportToCSV = () => {
    try {
      // Escapar valores para CSV
      const escapeCSV = (str: string) => {
        if (str === null || str === undefined) return ''
        const stringValue = String(str)
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`
        }
        return stringValue
      }

      // Cabeceras
      const headers = ['Fecha', 'Usuario', 'Email', 'Acción', 'Tipo de Recurso', 'ID del Recurso', 'IP', 'Navegador', 'Detalles']
      
      // Datos
      const rows = filteredLogs.map(log => [
        new Date(log.created_at).toLocaleString('es-ES'),
        escapeCSV(log.profiles?.full_name || 'Desconocido'),
        escapeCSV(log.profiles?.email || 'Desconocido'),
        escapeCSV(log.action),
        escapeCSV(log.resource_type || '-'),
        escapeCSV(log.resource_id || '-'),
        escapeCSV(log.ip_address || '-'),
        escapeCSV(log.user_agent || '-'),
        escapeCSV(JSON.stringify(log.details || {}))
      ])

      // Construir CSV
      const csv = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n')

      // Descargar
      const BOM = '\uFEFF' // BOM para UTF-8
      const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      toast.success(`${filteredLogs.length} logs exportados a CSV exitosamente`)
    } catch (error: any) {
      console.error('Error exporting CSV:', error)
      toast.error('Error al exportar logs')
    }
  }

  const exportToJSON = () => {
    try {
      const data = filteredLogs.map(log => ({
        fecha: new Date(log.created_at).toISOString(),
        fecha_legible: new Date(log.created_at).toLocaleString('es-ES'),
        usuario: {
          id: log.user_id,
          nombre: log.profiles?.full_name || 'Desconocido',
          email: log.profiles?.email || 'Desconocido'
        },
        accion: log.action,
        recurso: {
          tipo: log.resource_type || null,
          id: log.resource_id || null
        },
        ip: log.ip_address || null,
        navegador: log.user_agent || null,
        detalles: log.details || {}
      }))

      const json = JSON.stringify(data, null, 2)
      const blob = new Blob([json], { type: 'application/json' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      toast.success(`${filteredLogs.length} logs exportados a JSON exitosamente`)
    } catch (error: any) {
      console.error('Error exporting JSON:', error)
      toast.error('Error al exportar logs')
    }
  }

  const handleExport = () => {
    if (exportFormat === 'csv') {
      exportToCSV()
    } else {
      exportToJSON()
    }
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Logs de Auditoría
            </h1>
            <p className="text-gray-600">
              Registro completo de acciones administrativas del sistema
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as 'csv' | 'json')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
            >
              <option value="csv">CSV (Excel)</option>
              <option value="json">JSON</option>
            </select>
            <button
              onClick={handleExport}
              disabled={filteredLogs.length === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-education text-white rounded-lg hover:bg-education/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4" />
              <span>Exportar ({filteredLogs.length})</span>
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Búsqueda */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar en logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
              />
            </div>

            {/* Filtro por acción */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent appearance-none"
              >
                <option value="all">Todas las acciones</option>
                {uniqueActions.map(action => (
                  <option key={action} value={action}>{action}</option>
                ))}
              </select>
            </div>

            {/* Filtro por tipo de recurso */}
            <div className="relative">
              <Activity className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={filterResource}
                onChange={(e) => setFilterResource(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent appearance-none"
              >
                <option value="all">Todos los recursos</option>
                {uniqueResources.map(resource => (
                  <option key={resource} value={resource}>{resource}</option>
                ))}
              </select>
            </div>

            {/* Fecha desde */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
              />
            </div>

            {/* Fecha hasta */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>
              Mostrando {filteredLogs.length} de {logs.length} registros
            </span>
            {(searchTerm || filterAction !== 'all' || filterResource !== 'all' || dateFrom || dateTo) && (
              <button
                onClick={() => {
                  setSearchTerm('')
                  setFilterAction('all')
                  setFilterResource('all')
                  setDateFrom('')
                  setDateTo('')
                }}
                className="text-education hover:text-education/80 font-medium"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        </div>

        {/* Tabla de logs */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-education mx-auto"></div>
              <p className="text-gray-600 mt-2">Cargando logs...</p>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="p-8 text-center">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No se encontraron logs con los filtros aplicados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha/Hora
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuario
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acción
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recurso
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IP
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Detalles
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLogs.map((log) => (
                    <>
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(log.created_at).toLocaleString('es-ES', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="h-4 w-4 text-gray-400 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {log.profiles?.full_name || 'Usuario sin nombre'}
                              </div>
                              <div className="text-sm text-gray-500">{log.profiles?.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getActionBadge(log.action)}`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.resource_type || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {log.ip_address || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                            className="text-education hover:text-education/80"
                          >
                            {expandedLog === log.id ? (
                              <ChevronUp className="h-5 w-5" />
                            ) : (
                              <ChevronDown className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                      </tr>
                      {expandedLog === log.id && (
                        <tr className="bg-gray-50">
                          <td colSpan={6} className="px-6 py-4">
                            <div className="text-sm">
                              <h4 className="font-semibold text-gray-900 mb-2">Detalles del Log:</h4>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-gray-600"><span className="font-medium">Resource ID:</span> {log.resource_id || '-'}</p>
                                  <p className="text-gray-600"><span className="font-medium">User Agent:</span> {log.user_agent || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-gray-600 font-medium mb-1">Detalles adicionales:</p>
                                  <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                                    {JSON.stringify(log.details, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}
